import React, { useRef, useEffect, useState } from 'react';

function daysBetween(a, b) { return Math.round((new Date(b + 'T00:00:00Z') - new Date(a + 'T00:00:00Z')) / 86400000); }
function addDaysIso(d, n) { return new Date(new Date(d + 'T00:00:00Z').getTime() + n * 86400000).toISOString().slice(0, 10); }
function todayIso() { return new Date().toISOString().slice(0, 10); }

// Gantt chart: planned (blue) vs actual (green on-time / red late) per activity.
// `project` = full project object with milestones[] (each with tasks[]).
export default function GanttChart({ project }) {
  const PX = 24;        // pixels per day
  const LABEL_W = 240;
  const scrollRef = useRef(null);
  const geomRef = useRef({ showToday: true, todayX: 0 });
  const [metrics, setMetrics] = useState({ sl: 0, sw: 1, cw: 1 });
  const syncMetrics = () => {
    const el = scrollRef.current;
    if (el) setMetrics({ sl: el.scrollLeft, sw: el.scrollWidth, cw: el.clientWidth });
  };
  // On load / project change, pick a sensible default view:
  //  • active/future project → scroll so TODAY sits near the left edge
  //  • completed/past project (today after all activity) → scroll to the end so
  //    the completion period is shown instead of an empty "today" tail.
  // Done after layout (rAF) so the wide content exists before we set scrollLeft.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => {
      const g = geomRef.current;
      el.scrollLeft = g.showToday
        ? Math.max(0, g.todayX - PX * 2)
        : Math.max(0, el.scrollWidth - el.clientWidth);
      syncMetrics();
    }));
    const onScroll = () => syncMetrics();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { cancelAnimationFrame(raf); el.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, [project]);
  const rows = [];
  for (const m of (project.milestones || [])) {
    const tasks = m.tasks || [];
    const doneCount = tasks.filter(t => t.done || t.pct_complete >= 100).length;
    const mPct = tasks.length ? Math.round((doneCount / tasks.length) * 100) : null;
    rows.push({ label: m.title, bold: true, indent: 0,
      ps: m.start_date, pe: m.end_date, as: m.actual_start, ae: m.actual_end,
      pct: mPct, done: mPct === 100 });
    for (const t of tasks) {
      const tPct = t.pct_complete != null ? t.pct_complete : (t.done ? 100 : 0);
      rows.push({ label: t.title, bold: false, indent: 1,
        ps: t.planned_start, pe: t.planned_end, as: t.actual_start, ae: t.actual_end,
        pct: tPct, done: !!t.done || tPct >= 100 });
    }
  }
  const allDates = [];
  rows.forEach(r => [r.ps, r.pe, r.as, r.ae].forEach(d => d && allDates.push(d)));
  if (allDates.length === 0) {
    return <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: 24 }}>
      No planned or actual dates yet. Add tasks with dates in the Tasks tab.
    </div>;
  }
  allDates.sort();
  const today = todayIso();
  const first = allDates[0];
  const last = allDates[allDates.length - 1];
  // A finished project's activity is entirely before today — don't stretch the
  // chart (and the default view) out to an empty "today" tail. Frame it to the
  // activity instead, so it opens on the period when the work was done.
  const min = today < first ? today : first;          // extend left only for a future project
  const max = last;                                   // never extend right past the activity
  const showToday = today >= min && today <= max;     // today only matters when it's in range
  const totalDays = daysBetween(min, max) + 2;
  const width = totalDays * PX;

  const ticks = [];
  for (let i = 0; i <= totalDays; i++) {
    const d = addDaysIso(min, i);
    if (i === 0 || d.slice(8, 10) === '01') ticks.push({ d, x: i * PX });
  }
  const weekTicks = [];
  for (let i = 0; i <= totalDays; i += 7) weekTicks.push({ x: i * PX, wk: i / 7 + 1 });
  const todayX = daysBetween(min, today) * PX;
  geomRef.current = { showToday, todayX };

  const bar = (start, end, color, top, h) => {
    if (!start || !end) return null;
    const left = daysBetween(min, start) * PX;
    const w = Math.max(PX * 0.5, (daysBetween(start, end) + 1) * PX);
    return <div style={{ position: 'absolute', left, top, width: w, height: h, background: color, borderRadius: 4 }} />;
  };

  const todayScrollX = LABEL_W + todayX;
  const jumpToToday = () => { const el = scrollRef.current; if (el) { el.scrollLeft = Math.max(0, todayX - PX * 2); syncMetrics(); } };

  return (
    <div>
    <Scrubber scrollRef={scrollRef} metrics={metrics} todayScrollX={todayScrollX} showToday={showToday} onToday={jumpToToday} />
    <div ref={scrollRef} style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
      <div style={{ minWidth: LABEL_W + width }}>
        {/* Header: month labels */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-app)' }}>
          <div style={{ width: LABEL_W, flexShrink: 0, padding: '8px 12px', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)',
                        position: 'sticky', left: 0, zIndex: 4, background: 'var(--bg-app)' }}>Activity</div>
          <div style={{ position: 'relative', width, height: 44 }}>
            {/* Month names (top row) */}
            {ticks.map((t, i) => (
              <div key={i} style={{ position: 'absolute', left: t.x, top: 0, height: 22, borderLeft: '1px solid var(--border-subtle)', paddingLeft: 4,
                                    fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
                {new Date(t.d + 'T00:00:00Z').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
              </div>
            ))}
            {/* Week numbers (bottom row) */}
            {weekTicks.map((t, i) => (
              <div key={'w' + i} style={{ position: 'absolute', left: t.x, top: 22, height: 22, borderLeft: '1px solid var(--border-subtle)', opacity: .7, paddingLeft: 3,
                                    fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
                Wk{t.wk}
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div style={{ position: 'relative' }}>
          {showToday && (
            <>
              <div style={{ position: 'absolute', left: LABEL_W + todayX, top: 0, bottom: 0, width: 2, background: 'rgba(220,38,38,.5)', zIndex: 2 }} />
              <div style={{ position: 'absolute', left: LABEL_W + todayX, top: -2, transform: 'translateX(-50%)', fontSize: 9, fontWeight: 800, color: '#dc2626', background: 'var(--bg-surface)', padding: '0 3px', borderRadius: 3, zIndex: 3 }}>TODAY</div>
            </>
          )}
          {rows.map((r, i) => {
            const actualColor = (r.ae && r.pe && r.ae > r.pe) ? '#dc2626' : '#16a34a';
            // Slipping = planned end passed, nothing finished (no actual end) and not complete.
            const overdue = !!r.pe && r.pe < today && !r.ae && (r.pct == null || r.pct < 100);
            // Planned bar geometry (for the light track + progress fill).
            let plannedLeft = 0, plannedW = 0;
            if (r.ps && r.pe) {
              plannedLeft = daysBetween(min, r.ps) * PX;
              plannedW = Math.max(PX * 0.5, (daysBetween(r.ps, r.pe) + 1) * PX);
            }
            const slipFill = 'repeating-linear-gradient(45deg,#f59e0b,#f59e0b 3px,#fde68a 3px,#fde68a 6px)';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--border-subtle)',
                                    background: r.bold ? 'var(--bg-app)' : 'var(--bg-surface)' }}>
                <div style={{ width: LABEL_W, flexShrink: 0, padding: `6px 12px 6px ${12 + r.indent * 18}px`,
                              fontSize: 12.5, fontWeight: r.bold ? 700 : 500, color: 'var(--text-primary)',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              position: 'sticky', left: 0, zIndex: 3, background: r.bold ? 'var(--bg-app)' : 'var(--bg-surface)' }}>
                  {r.bold ? '◆ ' : ''}{r.label}
                  {overdue && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#d97706' }}>OVERDUE</span>}
                </div>
                <div style={{ position: 'relative', width, height: 34 }}>
                  {weekTicks.map((t, j) => <div key={'w' + j} style={{ position: 'absolute', left: t.x, top: 0, bottom: 0, borderLeft: '1px solid var(--border-subtle)', opacity: .22 }} />)}
                  {ticks.map((t, j) => <div key={j} style={{ position: 'absolute', left: t.x, top: 0, bottom: 0, borderLeft: '1px solid var(--border-subtle)', opacity: .5 }} />)}
                  {/* Planned track: light blue (or amber hatch when slipping) */}
                  {plannedW > 0 && (
                    <div style={{ position: 'absolute', left: plannedLeft, top: 6, width: plannedW, height: 8, borderRadius: 4,
                                  background: overdue ? slipFill : '#cfe0fb', border: overdue ? '1.5px solid #d97706' : 'none' }} />
                  )}
                  {/* Progress fill: solid blue proportional to % complete */}
                  {plannedW > 0 && r.pct > 0 && (
                    <div style={{ position: 'absolute', left: plannedLeft, top: 6, width: Math.round(plannedW * Math.min(r.pct, 100) / 100), height: 8, borderRadius: 4, background: '#2563eb' }} />
                  )}
                  {/* % label while in progress and not yet closed */}
                  {plannedW > 0 && r.pct > 0 && r.pct < 100 && !r.ae && (
                    <div style={{ position: 'absolute', left: plannedLeft + Math.round(plannedW * r.pct / 100) + 3, top: 4, fontSize: 9, fontWeight: 800, color: '#2563eb' }}>{r.pct}%</div>
                  )}
                  {overdue && plannedW > 0 && (
                    <div style={{ position: 'absolute', left: plannedLeft + plannedW + 4, top: 3, fontSize: 11 }} title="Overdue — planned end passed">⚠️</div>
                  )}
                  {/* Actual bar */}
                  {bar(r.as, r.ae, actualColor, 19, 8)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </div>
  );
}

// Draggable timeline scrubber — a mini horizontal bar whose thumb represents the
// visible window; drag it (or click the track) to pan the Gantt left/right.
function Scrubber({ scrollRef, metrics, todayScrollX, showToday, onToday }) {
  const trackRef = useRef(null);
  const maxScroll = Math.max(1, metrics.sw - metrics.cw);
  const widthPct = metrics.sw > 0 ? Math.min(100, (metrics.cw / metrics.sw) * 100) : 100;
  const leftPct = metrics.sw > 0 ? Math.min(100 - widthPct, (metrics.sl / metrics.sw) * 100) : 0;
  const todayPct = metrics.sw > 0 ? Math.max(0, Math.min(100, (todayScrollX / metrics.sw) * 100)) : 0;

  function dragThumb(e) {
    e.preventDefault();
    const el = scrollRef.current, track = trackRef.current;
    if (!el || !track) return;
    const tw = track.getBoundingClientRect().width;
    const startX = e.clientX != null ? e.clientX : e.touches[0].clientX;
    const startSL = el.scrollLeft;
    const move = (ev) => {
      const x = ev.clientX != null ? ev.clientX : (ev.touches && ev.touches[0].clientX);
      if (x == null) return;
      el.scrollLeft = startSL + ((x - startX) / tw) * el.scrollWidth;
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', up);
  }
  function clickTrack(e) {
    if (e.target !== trackRef.current) return;
    const el = scrollRef.current, track = trackRef.current;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.scrollLeft = Math.max(0, Math.min(maxScroll, ratio * el.scrollWidth - el.clientWidth / 2));
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', flexShrink: 0 }}>Timeline</span>
      <div ref={trackRef} onMouseDown={clickTrack}
        style={{ position: 'relative', flex: 1, height: 16, background: 'var(--n-100)', borderRadius: 8, cursor: 'pointer', overflow: 'hidden' }}
        title="Drag the bar, or click anywhere, to scroll the Gantt">
        {/* today marker */}
        {showToday && <div style={{ position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0, width: 2, background: 'rgba(220,38,38,.7)' }} />}
        {/* visible-window thumb */}
        <div onMouseDown={dragThumb} onTouchStart={dragThumb}
          style={{ position: 'absolute', left: `${leftPct}%`, top: 2, height: 12, width: `${Math.max(widthPct, 6)}%`,
                   background: 'var(--kg-600, #0d7a28)', borderRadius: 7, cursor: 'grab', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
      </div>
      {showToday && <button onClick={onToday} className="btn btn-secondary btn-sm" style={{ flexShrink: 0, fontSize: 12 }}>Today</button>}
    </div>
  );
}

// Small reusable legend row for the Gantt.
export function GanttLegend() {
  const item = (c, l) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 16, height: 8, background: c, borderRadius: 3, display: 'inline-block' }} /> {l}
    </span>
  );
  return (
    <div style={{ display: 'flex', gap: 16, fontSize: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      {item('#cfe0fb', 'Planned')}
      {item('#2563eb', 'Progress (% done)')}
      {item('#16a34a', 'Actual (on time)')}
      {item('#dc2626', 'Actual (late)')}
      {item('repeating-linear-gradient(45deg,#f59e0b,#f59e0b 3px,#fde68a 3px,#fde68a 6px)', 'Slipping (overdue, open)')}
    </div>
  );
}
